import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { CricketHeader } from '#/components/layout/CricketHeader'
import { BottomNav } from '#/components/layout/BottomNav'
import { TossModal } from '#/components/ui/TossModal'
import { AutocompleteSelect } from '#/components/ui/AutocompleteSelect'
import { useCricketStore } from '#/store/cricketStore'

export const Route = createFileRoute('/')({
  component: NewMatchScreen,
})

function NewMatchScreen() {
  const navigate = useNavigate()
  const initNewMatch = useCricketStore((state) => state.initNewMatch)
  const teamsList = useCricketStore((state) => state.teamsList)

  const matchSettings = useCricketStore((state) => state.matchSettings)

  const [hostTeam, setHostTeam] = useState('')
  const [visitorTeam, setVisitorTeam] = useState('')
  const [tossWinner, setTossWinner] = useState<'host' | 'visitor'>('host')
  const [optedTo, setOptedTo] = useState<'bat' | 'bowl'>('bat')
  const [overs, setOvers] = useState<number | string>(8)
  const [playersPerTeam, setPlayersPerTeam] = useState<number | string>(matchSettings.playersPerTeam || 11)
  const [errorMessage, setErrorMessage] = useState('')
  const [showTossModal, setShowTossModal] = useState(false)

  const handleStartMatch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!hostTeam.trim() || !visitorTeam.trim()) {
      setErrorMessage('Please enter both team names')
      return
    }

    if (hostTeam.trim().toLowerCase() === visitorTeam.trim().toLowerCase()) {
      setErrorMessage('Host Team and Visitor Team cannot be the same')
      return
    }

    setErrorMessage('')
    initNewMatch({
      hostTeam: hostTeam.trim(),
      visitorTeam: visitorTeam.trim(),
      tossWinner,
      tossDecision: optedTo,
      totalOvers: Number(overs) || 8,
      playersPerTeam: Number(playersPerTeam) || 11,
    })

    // Navigate to opening players screen
    navigate({ to: '/match/openers' })
  }

  return (
    <div className="flex-1 flex flex-col pb-20">
      <CricketHeader title="Cricket scorer" onOpenToss={() => setShowTossModal(true)} />

      {/* 3D Coin Toss Modal */}
      <TossModal
        isOpen={showTossModal}
        onClose={() => setShowTossModal(false)}
      />

      {/* Auto-suggest datalist for teams */}
      <datalist id="suggested-teams">
        {teamsList.map((t) => (
          <option key={t.id} value={t.name} />
        ))}
      </datalist>

      <form onSubmit={handleStartMatch} className="p-4 space-y-5 flex-1">
        {errorMessage && (
          <div className="bg-red-50 text-red-700 text-xs font-semibold px-3 py-2 rounded-lg border border-red-200">
            {errorMessage}
          </div>
        )}

        {/* Teams Section */}
        <div>
          <label className="text-sm font-semibold theme-text-primary block mb-2">
            Teams
          </label>
          <div className="theme-card rounded-lg p-4 shadow-sm space-y-4">
            <div>
              <AutocompleteSelect
                placeholder="Host Team"
                value={hostTeam}
                onChange={setHostTeam}
                options={teamsList.map((t) => t.name)}
                required
              />
            </div>
            <div>
              <AutocompleteSelect
                placeholder="Visitor Team"
                value={visitorTeam}
                onChange={setVisitorTeam}
                options={teamsList.map((t) => t.name)}
                required
              />
            </div>
          </div>
        </div>

        {/* Toss won by? */}
        <div>
          <label className="text-sm font-semibold theme-text-primary block mb-2">
            Toss won by?
          </label>
          <div className="theme-card rounded-lg p-3.5 shadow-sm flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="radio"
                name="tossWon"
                checked={tossWinner === 'host'}
                onChange={() => setTossWinner('host')}
                className="size-5"
              />
              <span className="text-sm font-medium truncate max-w-[120px]">
                {hostTeam || 'Host Team'}
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="radio"
                name="tossWon"
                checked={tossWinner === 'visitor'}
                onChange={() => setTossWinner('visitor')}
                className="size-5"
              />
              <span className="text-sm font-medium truncate max-w-[120px]">
                {visitorTeam || 'Visitor Team'}
              </span>
            </label>
          </div>
        </div>

        {/* Opted to? */}
        <div>
          <label className="text-sm font-semibold theme-text-primary block mb-2">
            Opted to?
          </label>
          <div className="theme-card rounded-lg p-3.5 shadow-sm flex items-center gap-8">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="radio"
                name="optedTo"
                checked={optedTo === 'bat'}
                onChange={() => setOptedTo('bat')}
                className="size-5"
              />
              <span className="text-sm font-medium">Bat</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="radio"
                name="optedTo"
                checked={optedTo === 'bowl'}
                onChange={() => setOptedTo('bowl')}
                className="size-5"
              />
              <span className="text-sm font-medium">Bowl</span>
            </label>
          </div>
        </div>

        {/* Overs & Players per team in 2 columns */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold theme-text-primary block mb-1.5">
              Overs?
            </label>
            <div className="theme-card rounded-lg p-3 shadow-sm">
              <input
                type="number"
                min="1"
                max="100"
                value={overs}
                onChange={(e) => setOvers(Number(e.target.value))}
                className="w-full border-b border-[var(--theme-border)] py-1 px-0 bg-transparent text-[var(--theme-text)] focus:outline-none focus:border-[var(--color-cricket-primary)] transition-colors text-base"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold theme-text-primary block mb-1.5">
              Players per team?
            </label>
            <div className="theme-card rounded-lg p-3 shadow-sm">
              <input
                type="number"
                min="1"
                max="25"
                value={playersPerTeam}
                onChange={(e) => setPlayersPerTeam(Number(e.target.value))}
                className="w-full border-b border-[var(--theme-border)] py-1 px-0 bg-transparent text-[var(--theme-text)] focus:outline-none focus:border-[var(--color-cricket-primary)] transition-colors text-base"
                required
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pt-2 flex items-center justify-between gap-4">
          <Link
            to="/settings"
            className="font-medium text-sm sm:text-base opacity-80 hover:opacity-100 hover:text-[var(--color-cricket-primary)] transition-colors px-2 py-2"
          >
            Advanced settings
          </Link>
          <button
            type="submit"
            className="theme-btn-primary font-semibold px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg shadow-sm active:scale-95 transition-all text-sm sm:text-base cursor-pointer"
          >
            Start match
          </button>
        </div>
      </form>

      <BottomNav />
    </div>
  )
}
